/**
 * ImageUpload component
 */

// React
import React, { ChangeEvent, FC } from 'react';
import Grid from '@mui/material/Grid2';

// Libraries
import { useDropzone } from 'react-dropzone';
import { Box, Button, ButtonProps, styled, Typography } from '@mui/material';

// Utils
import { AWS_S3 } from 'utils/constants';

export const TypographyCustom = styled(Typography)({
  marginBottom: '20px',
  fontWeight: '500',
  fontSize: '16px',
  color: '#666666',
});

export const BoxPreviewImage = styled(Box)({
  border: '2px dashed rgba(128, 128, 128, 0.5)',
  height: '163px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  width: '100%',
});

export const BoxPreviewUploadImage = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '10px',
  padding: '10px',
});

export const TypographyInfoImageCustom = styled(Typography)({
  fontWeight: '500',
  fontSize: '16px',
  color: '#666666',
});

export const ButtonUploadAndPreviewImage = styled(Button)<ButtonProps>(({ theme }) => ({
  height: '47px',
  width: '181px',
  fontSize: '16px',
  borderRadius: '10px',
  border: '1px solid rgba(128, 128, 128, 0.3)',
  background: 'rgba(128, 128, 128, 0.1)',
  color: '#666666',
  [theme.breakpoints.down('xl')]: {
    width: 'unset',
    padding: '0 10px',
  },
  [theme.breakpoints.down('md')]: {
    width: 'unset',
    padding: '0 10px',
    fontSize: '14px',
    height: '40px',
  },
}));

export const BoxButtonUpload = styled(Box)(({ theme }) => ({
  marginTop: '20px',
  display: 'flex',
  justifyContent: 'start',
  gap: '20px',
  [theme.breakpoints.down('md')]: {
    justifyContent: 'start',
    gap: '24px',
  },
}));

export const ButtonDeleteImage = styled(Button)(({ theme }) => ({
  height: '47px',
  width: '181px',
  color: '#666666',
  fontSize: '16px',
  border: '1px solid rgba(128, 128, 128, 0.3)',
  borderRadius: '10px',
  background: 'rgba(128, 128, 128, 0.1)',
  [theme.breakpoints.down('xl')]: {
    width: 'unset',
    padding: '0 10px',
  },
  [theme.breakpoints.down('md')]: {
    width: 'unset',
    padding: '0 10px',
    fontSize: '14px',
    height: '40px',
  },
}));

const FileUploadContainer = styled(Box)(({ theme }) => ({
  marginLeft: '60px',
  width: '100%',
  padding: '5px',
  [theme.breakpoints.down('md')]: {
    marginLeft: '0px',
  },
}));

// Interface
interface ImageUploadProps {
  title: string;
  handleFileChange: (_e: ChangeEvent<HTMLInputElement>, _iconType: string) => void;
  handleButtonDeleteImage: (_name: string) => void;
  handleButtonPreviewImage: (_image: string) => void;
  icon: string;
  iconName: string;
  file: any;
  size: any;
  error?: any;
  isShowMessage?: boolean;
}

// Component
const ImageUpload: FC<ImageUploadProps> = ({
  title,
  handleFileChange,
  handleButtonDeleteImage,
  handleButtonPreviewImage,
  icon,
  iconName,
  file,
  size,
  error,
  isShowMessage = false,
}) => {
  let pathIcon = '';
  if (icon !== AWS_S3.BASE_URL) {
    pathIcon = icon;
  }

  const onDrop = (acceptedFiles: File[]) => {
    if (acceptedFiles && acceptedFiles[0]) {
      const event = {
        target: {
          files: acceptedFiles,
        },
      } as unknown as ChangeEvent<HTMLInputElement>;
      handleFileChange(event, iconName);
    }
  };

  const pathIconName = pathIcon.replace(`${AWS_S3.BASE_URL}/services/`, '');
  const fileName = file ? file?.name : pathIconName;
  const fileType = file ? file?.type?.split('/')[1] : pathIconName.split('.')[1];
  const fileSize = file ? (file?.size / 1024).toFixed(2) : size;

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
  });

  return (
    <Grid
      container
      size={12}
      rowSpacing={{ xs: 1 }}
      columnSpacing={1}
      marginBottom={{ xs: 3, md: 5 }}
    >
      <Grid size={{ xs: 12, lg: 3 }}>
        <TypographyCustom>{title}</TypographyCustom>
      </Grid>
      <Grid size={{ xs: 12, md: 5 }}>
        <BoxPreviewImage {...getRootProps()} sx={{ cursor: 'pointer' }}>
          {pathIcon ? (
            <img style={{ width: '100px', height: 'auto' }} src={pathIcon} alt="Image" />
          ) : (
            <BoxPreviewUploadImage>
              <TypographyInfoImageCustom variant="body1">
                ここにファイルをドラック＆ドロップ
                <TypographyInfoImageCustom sx={{ display: 'flex', justifyContent: 'center' }}>
                  または
                </TypographyInfoImageCustom>
              </TypographyInfoImageCustom>
              <ButtonUploadAndPreviewImage component="label">
                ファイルを選択
              </ButtonUploadAndPreviewImage>
              <input type="file" hidden {...getInputProps()} />
              <TypographyInfoImageCustom variant="body1">
                ※アップロードできるファイルの形式は、画像（JPEG/PNG）のみです。
              </TypographyInfoImageCustom>
            </BoxPreviewUploadImage>
          )}
        </BoxPreviewImage>
        {isShowMessage && <span style={{ color: '#D95153', fontSize: '12px' }}>{error}</span>}
      </Grid>

      <Grid sx={{ display: 'flex', alignItems: 'end' }} size={{ xs: 12, md: 5, lg: 4 }}>
        {pathIcon && (
          <FileUploadContainer>
            {(file || icon) && (
              <Box>
                <TypographyInfoImageCustom
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: '350px',
                  }}
                  variant="body1"
                >
                  ファイル名: {fileName}
                </TypographyInfoImageCustom>
                <TypographyInfoImageCustom variant="body1">
                  ファイル種類: {fileType.toUpperCase()}
                </TypographyInfoImageCustom>
                <TypographyInfoImageCustom variant="body1">
                  ファイルサイズ: {fileSize}kb
                </TypographyInfoImageCustom>
              </Box>
            )}

            <BoxButtonUpload>
              <ButtonUploadAndPreviewImage
                onClick={() => {
                  handleButtonPreviewImage(icon);
                }}
              >
                ファイルを確認
              </ButtonUploadAndPreviewImage>
              <ButtonDeleteImage
                onClick={() => {
                  handleButtonDeleteImage(iconName);
                }}
              >
                ファイルを削除
              </ButtonDeleteImage>
            </BoxButtonUpload>
          </FileUploadContainer>
        )}
      </Grid>
    </Grid>
  );
};

export default ImageUpload;
